import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  List,
  Calendar,
  Package,
  Eye,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Search,
  Save
} from 'lucide-react';
import Modal from '../components/Modal';
import QuickOrderInput from '../components/QuickOrderInput';

function Monitor() {
  const [ordini, setOrdini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [consegneOggi, setConsegneOggi] = useState([]);
  const [showCalendario, setShowCalendario] = useState(false);
  const [consegneGiornaliere, setConsegneGiornaliere] = useState({});
  const [dataSelezionata, setDataSelezionata] = useState(new Date().toISOString().split('T')[0]);
  const [meseCalendario, setMeseCalendario] = useState(new Date().getMonth());
  const [annoCalendario, setAnnoCalendario] = useState(new Date().getFullYear());

  // Stati per l'aggiunta prodotti
  const [showAddProdottiModal, setShowAddProdottiModal] = useState(false);
  const [ordineDaModificare, setOrdineDaModificare] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [prodottoPendingTipologia, setProdottoPendingTipologia] = useState(null);
  const [showTipologiaModal, setShowTipologiaModal] = useState(false);

  // Stati per cancellazione prodotti
  const [showDeleteProdottoModal, setShowDeleteProdottoModal] = useState(false);
  const [prodottoToDelete, setProdottoToDelete] = useState(null);

  useEffect(() => {
    loadOrdini();
    loadFornitori();
    loadProdotti();
    loadConsegneOggi();
  }, []);

  // Effetto per la ricerca prodotti
  useEffect(() => {
    const searchProducts = () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      const filtered = prodotti.filter(prodotto =>
        prodotto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prodotto.categoria.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 10); // Limita a 10 risultati

      setSearchResults(filtered);
      setShowSearchResults(filtered.length > 0);
    };

    const debounceTimer = setTimeout(searchProducts, 200);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, prodotti]);


  const loadOrdini = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ordini', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Ordina gli ordini con l'ultimo in alto
        const ordiniOrdinati = response.data.data.sort((a, b) => {
          const dataA = new Date(a.data_ordine + ' ' + a.created_at);
          const dataB = new Date(b.data_ordine + ' ' + b.created_at);
          return dataB - dataA;
        });
        setOrdini(ordiniOrdinati);
      }
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
      toast.error('Errore nel caricamento degli ordini');
    }
  };

  const loadFornitori = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/fornitori', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFornitori(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  };

  const loadProdotti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/prodotti', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProdotti(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
    }
  };


  const loadConsegneOggi = async () => {
    try {
      const token = localStorage.getItem('token');
      const oggi = new Date().toISOString().split('T')[0];

      // Carica ordini completati di oggi
      const response = await axios.get('/api/ordini', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filtra ordini completati di oggi
        const ordiniOggi = response.data.data.filter(ordine =>
          ordine.stato === 'completed' && ordine.data_ordine === oggi
        );

        // Raggruppa prodotti per fornitore
        const consegnePerFornitore = {};
        ordiniOggi.forEach(ordine => {
          ordine.prodotti?.forEach(prodotto => {
            if (prodotto.fornitore_id) {
              const fornitoreId = prodotto.fornitore_id;
              if (!consegnePerFornitore[fornitoreId]) {
                consegnePerFornitore[fornitoreId] = {
                  fornitore_nome: prodotto.fornitore_nome,
                  prodotti: []
                };
              }
              consegnePerFornitore[fornitoreId].prodotti.push(prodotto);
            }
          });
        });

        setConsegneOggi(Object.values(consegnePerFornitore));
      }
    } catch (error) {
      console.error('Errore caricamento consegne:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConsegneGiornaliere = async (data) => {
    try {
      const token = localStorage.getItem('token');

      // Carica tutti gli ordini
      const response = await axios.get('/api/ordini', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filtra ordini inviati o completati per quella data
        // Normalizza la data per il confronto (rimuovi eventuali problemi di fuso orario)
        const dataNormalizzata = data.split('T')[0];
        const ordiniDelGiorno = response.data.data.filter(ordine => {
          const dataOrdine = ordine.data_ordine ? ordine.data_ordine.split('T')[0] : null;
          return (ordine.stato === 'sent' || ordine.stato === 'completed') && dataOrdine === dataNormalizzata;
        });

        // Carica i dettagli completi di ogni ordine per ottenere i prodotti
        const ordiniConProdotti = await Promise.all(
          ordiniDelGiorno.map(async (ordine) => {
            try {
              const dettagliResponse = await axios.get(`/api/ordini/${ordine.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (dettagliResponse.data.success) {
                return dettagliResponse.data.data;
              }
              return ordine;
            } catch (error) {
              console.error(`Errore caricamento dettagli ordine ${ordine.id}:`, error);
              return ordine;
            }
          })
        );

        const consegnePerFornitore = {};
        ordiniConProdotti.forEach(ordine => {
          if (ordine.prodotti && Array.isArray(ordine.prodotti)) {
            ordine.prodotti.forEach(prodotto => {
              if (prodotto.fornitore_id) {
                const fornitoreId = prodotto.fornitore_id;
                if (!consegnePerFornitore[fornitoreId]) {
                  consegnePerFornitore[fornitoreId] = {
                    fornitore_id: fornitoreId,
                    fornitore_nome: prodotto.fornitore_nome || 'Sconosciuto',
                    prodotti: []
                  };
                }
                consegnePerFornitore[fornitoreId].prodotti.push(prodotto);
              }
            });
          }
        });

        setConsegneGiornaliere(prev => ({
          ...prev,
          [data]: Object.values(consegnePerFornitore)
        }));
      }
    } catch (error) {
      console.error('Errore caricamento consegne giornaliere:', error);
      toast.error('Errore nel caricamento delle consegne');
    }
  };

  const viewOrdineDetails = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSelectedOrdine(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Errore caricamento dettagli ordine:', error);
      toast.error('Errore nel caricamento dei dettagli');
    }
  };


  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'draft':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatoText = (stato) => {
    switch (stato) {
      case 'draft':
        return 'Bozza';
      case 'sent':
        return 'Inviato';
      case 'completed':
        return 'Completato';
      default:
        return 'Errore';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Gestisci sia date in formato ISO che date già formattate
    const data = dateString.includes('T') 
      ? new Date(dateString) 
      : new Date(dateString + 'T12:00:00'); // Aggiungi ora per evitare problemi di fuso orario
    return data.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Stati per i prodotti dell'ordine corrente
  const [prodottiOrdineCorrente, setProdottiOrdineCorrente] = useState([]);
  const [ultimoProdottoInserito, setUltimoProdottoInserito] = useState(null);
  const [prodottiModificati, setProdottiModificati] = useState(false); // Flag per tracciare modifiche non salvate

  // Funzioni per aggiungere prodotti
  const openAddProdottiModal = async (ordineId) => {
    setOrdineDaModificare(ordineId);
    setShowAddProdottiModal(true);
    setSearchTerm('');
    setShowSearchResults(false);
    setProdottiModificati(false); // Reset flag modifiche

    // Assicurati che i prodotti siano caricati prima di caricare l'ordine
    if (prodotti.length === 0) {
      await loadProdotti();
    }

    // Carica i prodotti attuali dell'ordine
    await loadProdottiOrdineCorrente(ordineId);
  };

  const salvaModificheOrdine = async () => {
    if (!ordineDaModificare) return;

    try {
      const token = localStorage.getItem('token');

      // Ottieni dettagli ordine corrente per le note personalizzate
      const ordineResponse = await axios.get(`/api/ordini/${ordineDaModificare}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!ordineResponse.data.success) {
        throw new Error('Errore nel recupero dell\'ordine');
      }

      const ordineCorrente = ordineResponse.data.data;

      // Prepara i prodotti da salvare così come sono (senza raggruppamento)
      // Il raggruppamento viene gestito solo quando si aggiunge un prodotto, non al salvataggio
      const prodottiDaSalvare = prodottiOrdineCorrente.map(prodotto => ({
        prodotto_id: prodotto.prodotto_id || prodotto.id,
        quantita: prodotto.quantita || 1,
        note: prodotto.note || null,
        fornitore_id: prodotto.fornitore_id || null,
        tipologia_scelta: prodotto.tipologia_scelta || prodotto.tipologia || null
      }));

      // Aggiorna l'ordine
      const updateResponse = await axios.put(`/api/ordini/${ordineDaModificare}`, {
        prodotti: prodottiDaSalvare,
        note_personalizzate: ordineCorrente.note_personalizzate || {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (updateResponse.data.success) {
        toast.success('Modifiche salvate con successo!');
        setProdottiModificati(false);

        // Ricarica tutto
        await loadOrdini();
        
        // Assicurati che i prodotti siano caricati
        if (prodotti.length === 0) {
          await loadProdotti();
        }
        
        // Ricarica i prodotti dell'ordine per aggiornare gli ID reali
        await loadProdottiOrdineCorrente(ordineDaModificare);

        // Aggiorna l'ultimo prodotto inserito con l'ID reale
        if (ultimoProdottoInserito) {
          const ordineAggiornato = await axios.get(`/api/ordini/${ordineDaModificare}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (ordineAggiornato.data.success) {
            const prodottiAggiornati = ordineAggiornato.data.data.prodotti || [];
            // Trova l'ultimo prodotto inserito (quello con ID più alto)
            if (prodottiAggiornati.length > 0) {
              const ultimoProdotto = prodottiAggiornati.sort((a, b) => b.id - a.id)[0];
              setUltimoProdottoInserito(ultimoProdotto.id);
            }
          }
        }

        // Aggiorna anche la modal dettagli se è aperta
        if (showDetails && selectedOrdine && selectedOrdine.id === ordineDaModificare) {
          await viewOrdineDetails(ordineDaModificare);
        }
      }
    } catch (error) {
      console.error('❌ [MONITOR] Errore salvataggio modifiche:', error);
      console.error('❌ [MONITOR] Dettagli errore:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Errore nel salvataggio delle modifiche');
    }
  };

  const closeAddProdottiModal = () => {
    // Se ci sono modifiche non salvate, chiedi conferma
    if (prodottiModificati) {
      const conferma = window.confirm(
        'Hai modifiche non salvate. Se chiudi ora, perderai tutte le modifiche.\n\nVuoi salvare prima di chiudere?'
      );
      
      if (conferma) {
        salvaModificheOrdine().then(() => {
          setShowAddProdottiModal(false);
          setOrdineDaModificare(null);
          setProdottiOrdineCorrente([]);
          setUltimoProdottoInserito(null);
          setProdottiModificati(false);
          setSearchTerm('');
          setShowSearchResults(false);
        });
        return;
      }
    }

    setShowAddProdottiModal(false);
    setOrdineDaModificare(null);
    setProdottiOrdineCorrente([]);
    setUltimoProdottoInserito(null);
    setProdottiModificati(false);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const loadProdottiOrdineCorrente = async (ordineId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/ordini/${ordineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Arricchisci i prodotti con le informazioni sulle tipologie disponibili
        const prodottiOrdine = response.data.data.prodotti || [];
        
        
        const prodottiArricchiti = prodottiOrdine.map(prodottoOrdine => {
          // L'id del prodotto nel catalogo è prodotto_id, l'id della riga ordine_prodotti è id
          const prodottoIdCatalogo = prodottoOrdine.prodotto_id;
          const prodottoCompleto = prodotti.find(p => p.id === prodottoIdCatalogo);
          
          // Se prodottoCompleto non è trovato, prova a caricarlo
          if (!prodottoCompleto && prodotti.length === 0) {
            // Se i prodotti non sono ancora caricati, aspetta
            return {
              ...prodottoOrdine,
              prodotto_id: prodottoIdCatalogo,
              id: prodottoOrdine.id // ID della riga ordine_prodotti
            };
          }

          return {
            ...prodottoOrdine,
            prodotto_id: prodottoIdCatalogo, // ID del prodotto nel catalogo
            id: prodottoOrdine.id, // ID della riga ordine_prodotti (per identificare il prodotto nell'ordine)
            tipologie_disponibili: prodottoCompleto?.tipologie_disponibili || [],
            tipologia_default_nome: prodottoCompleto?.tipologia_default_nome || null
          };
        });

        setProdottiOrdineCorrente(prodottiArricchiti);
      }
    } catch (error) {
      console.error('❌ [MONITOR] Errore caricamento prodotti ordine:', error);
    }
  };

  const aggiornaQuantitaProdotto = (prodottoId, nuovaQuantita) => {
    if (nuovaQuantita < 1) return;

    // Aggiorna solo in memoria locale
    const prodottiAggiornati = prodottiOrdineCorrente.map(prodotto =>
      prodotto.id === prodottoId
        ? { ...prodotto, quantita: nuovaQuantita }
        : prodotto
    );

    setProdottiOrdineCorrente(prodottiAggiornati);
    setProdottiModificati(true);
  };

  const rimuoviProdottoOrdine = (prodottoId) => {
    const prodotto = prodottiOrdineCorrente.find(item => item.id === prodottoId);
    setProdottoToDelete(prodotto);
    setShowDeleteProdottoModal(true);
  };

  const executeRemoveProdotto = () => {
    if (!prodottoToDelete) return;

    // Rimuovi solo in memoria locale
    const prodottiAggiornati = prodottiOrdineCorrente.filter(prodotto => prodotto.id !== prodottoToDelete.id);
    setProdottiOrdineCorrente(prodottiAggiornati);
    setProdottiModificati(true);
    
    // Se il prodotto rimosso era "ultimo inserito", rimuovilo
    if (ultimoProdottoInserito === prodottoToDelete.id) {
      setUltimoProdottoInserito(null);
    }

    setShowDeleteProdottoModal(false);
    setProdottoToDelete(null);
  };

  const aggiornaNoteProdotto = (prodottoId, note) => {
    // Aggiorna solo in memoria locale
    const prodottiAggiornati = prodottiOrdineCorrente.map(prodotto =>
      prodotto.id === prodottoId
        ? { ...prodotto, note }
        : prodotto
    );

    setProdottiOrdineCorrente(prodottiAggiornati);
    setProdottiModificati(true);
  };

  const aggiornaTipologiaProdotto = (prodottoId, tipologia) => {
    // Aggiorna solo in memoria locale
    const prodottiAggiornati = prodottiOrdineCorrente.map(prodotto =>
      prodotto.id === prodottoId
        ? { ...prodotto, tipologia_scelta: tipologia }
        : prodotto
    );

    setProdottiOrdineCorrente(prodottiAggiornati);
    setProdottiModificati(true);
  };

  const getUltimoProdottoInserito = () => {
    if (!ultimoProdottoInserito) return null;
    return prodottiOrdineCorrente.find(item => item.id === ultimoProdottoInserito);
  };

  const groupByFornitore = () => {
    const grouped = {};
    // Escludi l'ultimo prodotto inserito dalla lista normale
    const prodottiDaMostrare = prodottiOrdineCorrente.filter(item => item.id !== ultimoProdottoInserito);
    
    prodottiDaMostrare.forEach(item => {
      const fornitoreId = item.fornitore_id || 'senza_fornitore';
      if (!grouped[fornitoreId]) {
        grouped[fornitoreId] = {
          fornitore_id: item.fornitore_id,
          fornitore: fornitori.find(f => f.id === item.fornitore_id),
          prodotti: []
        };
      }
      grouped[fornitoreId].prodotti.push(item);
    });
    
    // Ordina i prodotti alfabeticamente per ogni fornitore
    Object.keys(grouped).forEach(fornitoreId => {
      grouped[fornitoreId].prodotti.sort((a, b) => a.nome.localeCompare(b.nome));
    });
    
    return grouped;
  };

  const aggiornaFornitoreProdotto = (prodottoId, fornitoreId) => {
    // Trova il fornitore selezionato
    const fornitoreSelezionato = fornitori.find(f => f.id === parseInt(fornitoreId));

    // Aggiorna solo in memoria locale
    const prodottiAggiornati = prodottiOrdineCorrente.map(prodotto =>
      prodotto.id === prodottoId
        ? {
            ...prodotto,
            fornitore_id: parseInt(fornitoreId),
            fornitore_nome: fornitoreSelezionato?.nome || null
          }
        : prodotto
    );

    setProdottiOrdineCorrente(prodottiAggiornati);
    setProdottiModificati(true);
  };

  // Gestisce i prodotti parsati dall'AI
  const handleProdottiParsed = async (prodottiParsati) => {
    for (const prodotto of prodottiParsati) {
      // Carica i dettagli completi del prodotto dal catalogo
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/prodotti/${prodotto.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          const prodottoCompleto = response.data.data;
          
          // Aggiungi il prodotto con la quantità specificata
          for (let i = 0; i < prodotto.quantita; i++) {
            await addProdottoToOrdine(prodottoCompleto);
          }
          
          // Se ci sono note, aggiungile all'ultimo prodotto inserito
          if (prodotto.note) {
            setProdottiOrdineCorrente(prev => {
              const updated = [...prev];
              const lastIndex = updated.length - 1;
              if (lastIndex >= 0) {
                updated[lastIndex].note = prodotto.note;
              }
              return updated;
            });
          }
        }
      } catch (error) {
        console.error(`Errore caricamento prodotto ${prodotto.nome}:`, error);
        toast.error(`Errore aggiunta ${prodotto.nome}`);
      }
    }
  };

  const addProdottoToOrdine = (prodotto) => {
    // Se il prodotto ha più tipologie, mostra il modale di selezione
    if (prodotto.tipologie_disponibili && prodotto.tipologie_disponibili.length > 1) {
      setProdottoPendingTipologia(prodotto);
      setShowTipologiaModal(true);
      setShowSearchResults(false);
      return;
    }

    // Se ha una sola tipologia o nessuna, aggiungi direttamente
    aggiungiProdotto(prodotto, null);
  };

  const aggiungiProdotto = async (prodotto, tipologiaScelta) => {
    try {
      const token = localStorage.getItem('token');

      // Ottieni dettagli ordine corrente
      const ordineResponse = await axios.get(`/api/ordini/${ordineDaModificare}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!ordineResponse.data.success) {
        throw new Error('Errore nel recupero dell\'ordine');
      }

      // Trova fornitore default per questa categoria
      const fornitoreDefault = await getFornitoreDefault(prodotto.categoria);

      // Prepara il nuovo prodotto
      let tipologiaFinale = tipologiaScelta;
      if (!tipologiaFinale && prodotto.tipologie_disponibili && prodotto.tipologie_disponibili.length > 0) {
        tipologiaFinale = prodotto.tipologia_default_nome || prodotto.tipologie_disponibili[0].nome;
      }
      if (!tipologiaFinale) {
        tipologiaFinale = prodotto.tipologia_default_nome || prodotto.tipologia;
      }

      const nuovoProdotto = {
        prodotto_id: prodotto.id,
        quantita: 1,
        note: prodotto.nota_default || '',
        nome: prodotto.nome,
        tipologia_scelta: tipologiaFinale,
        categoria: prodotto.categoria,
        fornitore_id: fornitoreDefault?.id || null,
        fornitore_nome: fornitoreDefault?.nome || null
      };

      // Controlla se il prodotto esiste già nell'ordine con stesso prodotto_id, tipologia e fornitore
      const prodottoEsistente = prodottiOrdineCorrente.find(p => {
        const stessoProdotto = (p.prodotto_id || p.id) === prodotto.id;
        const stessaTipologia = (p.tipologia_scelta || p.tipologia) === tipologiaFinale;
        const stessoFornitore = (p.fornitore_id || null) === (fornitoreDefault?.id || null);
        return stessoProdotto && stessaTipologia && stessoFornitore;
      });

      if (prodottoEsistente) {
        // Se esiste già con stesso prodotto_id, tipologia e fornitore, aumenta la quantità
        const prodottiAggiornati = prodottiOrdineCorrente.map(p =>
          p.id === prodottoEsistente.id
            ? { ...p, quantita: (p.quantita || 1) + 1 }
            : p
        );

        setProdottiOrdineCorrente(prodottiAggiornati);
        setProdottiModificati(true);

        // Imposta come ultimo inserito
        setUltimoProdottoInserito(prodottoEsistente.id);

        // Chiudi i risultati di ricerca
        setSearchTerm('');
        setShowSearchResults(false);

        toast.success(`Quantità di "${prodotto.nome}" aumentata a ${prodottoEsistente.quantita + 1} (non salvato)`);
      } else {
        // Se non esiste, aggiungi come nuovo prodotto
        // Genera un ID temporaneo univoco per il nuovo prodotto
        const nuovoProdottoConId = {
          ...nuovoProdotto,
          id: `temp_${Date.now()}_${Math.random()}`, // ID temporaneo
          prodotto_id: prodotto.id
        };

        const nuoviProdotti = [...prodottiOrdineCorrente, nuovoProdottoConId];
        setProdottiOrdineCorrente(nuoviProdotti);
        setProdottiModificati(true);

        // Imposta come ultimo inserito
        setUltimoProdottoInserito(nuovoProdottoConId.id);

        // Chiudi i risultati di ricerca
        setSearchTerm('');
        setShowSearchResults(false);

        toast.success(`"${prodotto.nome}" aggiunto all'ordine (non salvato)`);
      }
    } catch (error) {
      console.error('Errore aggiunta prodotto:', error);
      toast.error('Errore nell\'aggiunta del prodotto');
    }
  };

  const handleSelezioneTipologia = (tipologiaNome) => {
    if (prodottoPendingTipologia) {
      aggiungiProdotto(prodottoPendingTipologia, tipologiaNome);
      setShowTipologiaModal(false);
      setProdottoPendingTipologia(null);
    }
  };

  // Funzione helper per renderizzare una card prodotto
  const renderProdottoCard = (prodotto) => {
    const fornitoriCategoria = fornitori.filter(f => 
      (Array.isArray(f.categorie) && f.categorie.includes(prodotto.categoria)) ||
      (f.categoria === prodotto.categoria && f.attivo)
    );

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-1">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900 mb-1">
              {prodotto.nome}
            </p>
            <p className="text-xs text-gray-500 mb-2">
              {prodotto.categoria}
            </p>

            {/* Selezione tipologia */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Tipologia:
              </label>
              {prodotto.tipologie_disponibili && Array.isArray(prodotto.tipologie_disponibili) && prodotto.tipologie_disponibili.length > 0 ? (
                <select
                  value={prodotto.tipologia_scelta || prodotto.tipologia || prodotto.tipologie_disponibili[0]?.nome || ''}
                  onChange={(e) => aggiornaTipologiaProdotto(prodotto.id, e.target.value)}
                  className="text-sm border border-blue-300 rounded px-3 py-2 w-full bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                  style={{ fontSize: '16px' }}
                >
                  {prodotto.tipologie_disponibili.map((tip, idx) => (
                    <option key={tip.id || tip.nome || idx} value={tip.nome}>
                      {tip.nome}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-500 py-2">
                  {prodotto.tipologia_scelta || prodotto.tipologia || 'Nessuna tipologia'}
                </p>
              )}
            </div>

            {/* Selezione fornitore */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Fornitore:
              </label>
              {fornitoriCategoria.length > 1 ? (
                <select
                  value={prodotto.fornitore_id || ''}
                  onChange={(e) => aggiornaFornitoreProdotto(prodotto.id, e.target.value)}
                  className="text-sm border border-blue-300 rounded px-3 py-2 w-full bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                  style={{ fontSize: '16px' }}
                >
                  <option value="">Seleziona fornitore...</option>
                  {fornitoriCategoria.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
              ) : fornitoriCategoria.length === 1 ? (
                <p className="text-sm text-blue-600 py-2">
                  {fornitoriCategoria[0].nome}
                </p>
              ) : (
                <p className="text-sm text-gray-400 py-2 italic">
                  Nessun fornitore disponibile
                </p>
              )}
            </div>

            {/* Campo note */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Note:
              </label>
              <input
                type="text"
                placeholder="Note per questo prodotto..."
                value={prodotto.note || ''}
                onChange={(e) => aggiornaNoteProdotto(prodotto.id, e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>

          <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
            {/* Controlli quantità */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => aggiornaQuantitaProdotto(prodotto.id, prodotto.quantita - 1)}
                className="w-12 h-12 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-full touch-manipulation"
              >
                <span className="text-xl font-bold">-</span>
              </button>

              <span className="w-16 text-center font-semibold text-gray-900 text-lg">
                {prodotto.quantita}
              </span>

              <button
                onClick={() => aggiornaQuantitaProdotto(prodotto.id, prodotto.quantita + 1)}
                className="w-12 h-12 flex items-center justify-center bg-green-100 hover:bg-green-200 text-green-600 rounded-full touch-manipulation"
              >
                <span className="text-xl font-bold">+</span>
              </button>
            </div>

            {/* Pulsante rimuovi */}
            <button
              onClick={() => rimuoviProdottoOrdine(prodotto.id)}
              className="w-12 h-12 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-full touch-manipulation"
              title="Rimuovi prodotto"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getFornitoreDefault = async (categoria) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/fornitori/default/${categoria}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
    } catch (error) {
      console.error('Errore recupero fornitore default:', error);
    }
    return null;
  };


  const cambiaMese = (direzione) => {
    if (direzione === 'prev') {
      if (meseCalendario === 0) {
        setMeseCalendario(11);
        setAnnoCalendario(annoCalendario - 1);
      } else {
        setMeseCalendario(meseCalendario - 1);
      }
    } else {
      if (meseCalendario === 11) {
        setMeseCalendario(0);
        setAnnoCalendario(annoCalendario + 1);
      } else {
        setMeseCalendario(meseCalendario + 1);
      }
    }
  };

  const renderCalendario = () => {
    const primoGiorno = new Date(annoCalendario, meseCalendario, 1);
    const ultimoGiorno = new Date(annoCalendario, meseCalendario + 1, 0);
    const giorniNelMese = ultimoGiorno.getDate();

    const giorniSettimana = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

    const celleCalendario = [];

    // Celle vuote per i giorni precedenti al primo del mese
    for (let i = 0; i < primoGiorno.getDay(); i++) {
      celleCalendario.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Ottieni tutti gli ordini inviati/completati per evidenziare i giorni nel calendario
    const ordiniConData = ordini.filter(o => 
      (o.stato === 'sent' || o.stato === 'completed') && o.data_ordine
    );

    // Celle per ogni giorno del mese
    for (let giorno = 1; giorno <= giorniNelMese; giorno++) {
      // Crea la data in modo locale per evitare problemi di fuso orario
      const anno = annoCalendario;
      const mese = String(meseCalendario + 1).padStart(2, '0');
      const giornoStr = String(giorno).padStart(2, '0');
      const dataString = `${anno}-${mese}-${giornoStr}`;
      
      const oggi = new Date();
      const oggiAnno = oggi.getFullYear();
      const oggiMese = String(oggi.getMonth() + 1).padStart(2, '0');
      const oggiGiorno = String(oggi.getDate()).padStart(2, '0');
      const oggiString = `${oggiAnno}-${oggiMese}-${oggiGiorno}`;
      
      const isOggi = dataString === oggiString && 
                     meseCalendario === oggi.getMonth() && 
                     annoCalendario === oggi.getFullYear();
      const isSelezionato = dataString === dataSelezionata;
      
      // Controlla se ci sono ordini per questo giorno
      const haOrdini = ordiniConData.some(ordine => {
        const dataOrdine = ordine.data_ordine ? ordine.data_ordine.split('T')[0] : null;
        return dataOrdine === dataString;
      });

      celleCalendario.push(
        <button
          key={giorno}
          onClick={() => {
            setDataSelezionata(dataString);
            loadConsegneGiornaliere(dataString);
            // Chiudi il calendario dopo la selezione
            setShowCalendario(false);
          }}
          className={`p-2 text-sm border rounded-lg transition-colors relative ${
            isOggi
              ? 'bg-blue-100 border-blue-300 text-blue-700 font-semibold'
              : isSelezionato
              ? 'bg-blue-100 border-blue-300 text-blue-700'
              : 'hover:bg-gray-50 border-gray-200'
          }`}
        >
          {giorno}
          {haOrdini && (
            <span className="absolute bottom-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
        </button>
      );
    }

    const oggi = new Date();
    const isMeseCorrente = meseCalendario === oggi.getMonth() && annoCalendario === oggi.getFullYear();

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => cambiaMese('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
            title="Mese precedente"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {new Date(annoCalendario, meseCalendario).toLocaleDateString('it-IT', {
                month: 'long',
                year: 'numeric'
              })}
            </h3>
            {!isMeseCorrente && (
              <button
                onClick={() => {
                  setMeseCalendario(oggi.getMonth());
                  setAnnoCalendario(oggi.getFullYear());
                }}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors touch-manipulation"
                title="Torna al mese corrente"
              >
                Oggi
              </button>
            )}
          </div>
          <button
            onClick={() => cambiaMese('next')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
            title="Mese successivo"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Giorni della settimana */}
        <div className="grid grid-cols-7 gap-1">
          {giorniSettimana.map(giorno => (
            <div key={giorno} className="p-2 text-center text-sm font-medium text-gray-500">
              {giorno}
            </div>
          ))}
        </div>

        {/* Celle del calendario */}
        <div className="grid grid-cols-7 gap-1">
          {celleCalendario}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con logo */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-start py-4">
            <img
              src="/images/logo-pizzeria.png"
              alt="Pizzeria ai Pirami"
              className="h-16 w-auto object-contain"
            />
            <div className="ml-4 flex items-center">
              <h1 className="text-2xl font-bold tracking-wide" style={{ color: '#B91C2E' }}>
                MONITOR GESTIONE ORDINI
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">

        {/* Layout diviso */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-220px)]">

          {/* Sezione Sinistra: Ordini */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
            <div className="bg-blue-600 text-white p-4 h-20 flex flex-col justify-center">
              <h2 className="text-xl font-semibold flex items-center">
                <List className="mr-2 h-5 w-5" />
                Ordini Fatti
              </h2>
              <p className="text-blue-100 text-sm mt-1">L'ultimo ordine appare in alto</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {ordini.length === 0 ? (
                <div className="text-center py-12">
                  <List className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun ordine trovato</h3>
                </div>
              ) : (
                <div className="space-y-3">
                  {ordini.map((ordine) => (
                    <div key={ordine.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {getStatoIcon(ordine.stato)}
                            <h3 className="text-lg font-medium text-gray-900">
                              Ordine #{ordine.id}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              ordine.stato === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                              ordine.stato === 'sent' ? 'bg-green-100 text-green-800' :
                              ordine.stato === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {getStatoText(ordine.stato)}
                            </span>
                          </div>

                          <div className="text-sm text-gray-500 mb-2">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(ordine.data_ordine)}
                            </div>
                            <div className="flex items-center mt-1">
                              <Package className="h-4 w-4 mr-1" />
                              {ordine.numero_prodotti} prodotti
                            </div>
                          </div>

                          {ordine.prodotti_lista && (
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {ordine.prodotti_lista}
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => viewOrdineDetails(ordine.id)}
                            className="btn btn-outline btn-sm flex items-center justify-center min-w-[80px]"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            <span className="text-xs">Dettagli</span>
                          </button>

                          {ordine.stato === 'draft' && (
                            <button
                              onClick={() => openAddProdottiModal(ordine.id)}
                              className="btn btn-outline btn-sm flex-shrink-0 flex items-center justify-center min-w-[80px]"
                            >
                              <Plus className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline text-xs">Aggiungi</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sezione Destra: Consegne */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
            <div className="bg-green-600 text-white p-4 h-20 flex flex-col justify-center">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold flex items-center">
                    <Truck className="mr-2 h-5 w-5" />
                    Consegne Giornaliere
                  </h2>
                  <p className="text-green-100 text-sm mt-1">
                    {consegneOggi.length > 0
                      ? `${consegneOggi.length} fornitori hanno consegnato oggi`
                      : 'Nessuna consegna oggi'
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowCalendario(!showCalendario)}
                  className="btn btn-sm bg-white/20 hover:bg-white/30 text-white ml-4"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  {showCalendario ? 'Nascondi' : 'Calendario'}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {showCalendario ? (
                <div className="space-y-4">
                  {renderCalendario()}
                </div>
              ) : consegneGiornaliere[dataSelezionata] ? (
                consegneGiornaliere[dataSelezionata].length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Consegne del {formatDate(dataSelezionata)}
                      </h3>
                      <button
                        onClick={() => setShowCalendario(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Cambia data
                      </button>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {consegneGiornaliere[dataSelezionata].map((consegna, index) => (
                        <div key={index} className="py-4 first:pt-0">
                          <div className="mb-3">
                            <h4 className="text-base font-semibold text-gray-900 flex items-center">
                              <Package className="h-4 w-4 mr-2 text-gray-500" />
                              {consegna.fornitore_nome}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {consegna.prodotti.length} prodott{consegna.prodotti.length === 1 ? 'o' : 'i'}
                            </span>
                          </div>
                          <ul className="space-y-2 ml-6">
                            {consegna.prodotti.map((prodotto, idx) => (
                              <li key={idx} className="text-sm text-gray-700">
                                <span className="font-medium">{prodotto.quantita}</span>
                                {prodotto.tipologia_scelta && (
                                  <span className="text-gray-500"> {prodotto.tipologia_scelta}</span>
                                )}
                                <span className="ml-1">{prodotto.nome}</span>
                                {prodotto.note && (
                                  <span className="ml-2 text-gray-500 italic">• 📝 {prodotto.note}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Truck className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna consegna per questa data</h3>
                    <p className="mt-1 text-sm text-gray-500 mb-4">
                      Consegne del {formatDate(dataSelezionata)}
                    </p>
                    <button
                      onClick={() => setShowCalendario(true)}
                      className="btn btn-outline btn-sm"
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Cambia data
                    </button>
                  </div>
                )
              ) : consegneOggi.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna consegna oggi</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Usa il calendario per vedere le consegne passate
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Consegne di oggi
                  </h3>
                  <div className="divide-y divide-gray-200">
                    {consegneOggi.map((consegna, index) => (
                      <div key={index} className="py-4 first:pt-0">
                        <div className="mb-3">
                          <h4 className="text-base font-semibold text-gray-900 flex items-center">
                            <Package className="h-4 w-4 mr-2 text-gray-500" />
                            {consegna.fornitore_nome}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {consegna.prodotti.length} prodott{consegna.prodotti.length === 1 ? 'o' : 'i'}
                          </span>
                        </div>
                        <ul className="space-y-2 ml-6">
                          {consegna.prodotti.map((prodotto, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              <span className="font-medium">{prodotto.quantita}</span>
                              {prodotto.tipologia_scelta && (
                                <span className="text-gray-500"> {prodotto.tipologia_scelta}</span>
                              )}
                              <span className="ml-1">{prodotto.nome}</span>
                              {prodotto.note && (
                                <span className="ml-2 text-gray-500 italic">• 📝 {prodotto.note}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Dettagli Ordine */}
        {showDetails && selectedOrdine && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-4">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetails(false)} />

              <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="bg-white px-6 pt-6 pb-4 sm:p-8 sm:pb-4 flex-shrink-0 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Dettagli Ordine #{selectedOrdine.id}
                    </h3>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 sm:px-8 sm:py-6">
                  <div className="space-y-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-2" />
                      Data: {formatDate(selectedOrdine.data_ordine)}
                    </div>

                    <div className="flex items-center text-sm text-gray-500">
                      {getStatoIcon(selectedOrdine.stato)}
                      <span className="ml-2">Stato: {getStatoText(selectedOrdine.stato)}</span>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Prodotti per Fornitore:</h4>
                      <div className="space-y-4">
                        {(() => {
                          const grouped = {};
                          selectedOrdine.prodotti?.forEach(prodotto => {
                            const fornitoreId = prodotto.fornitore_id || 'senza_fornitore';
                            if (!grouped[fornitoreId]) {
                              grouped[fornitoreId] = {
                                fornitore_id: prodotto.fornitore_id,
                                fornitore: fornitori.find(f => f.id === prodotto.fornitore_id),
                                prodotti: []
                              };
                            }
                            grouped[fornitoreId].prodotti.push(prodotto);
                          });

                          Object.keys(grouped).forEach(fornitoreId => {
                            grouped[fornitoreId].prodotti.sort((a, b) => a.nome.localeCompare(b.nome));
                          });

                          return Object.entries(grouped).map(([fornitoreId, gruppo]) => {
                            if (!gruppo.fornitore) return null;

                            const notaKey = `fornitore_${fornitoreId}`;
                            const notaFornitore = selectedOrdine.note_personalizzate?.[notaKey] || '';

                            return (
                              <div key={fornitoreId} className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
                                  <div className="flex items-center">
                                    <Package className="h-5 w-5 mr-2 text-blue-600" />
                                    <h5 className="text-sm font-semibold text-blue-900">
                                      {gruppo.fornitore.nome}
                                    </h5>
                                    <span className="ml-auto text-xs text-blue-600 font-medium">
                                      {gruppo.prodotti.length} prodott{gruppo.prodotti.length === 1 ? 'o' : 'i'}
                                    </span>
                                  </div>
                                </div>

                                <div className="p-3 bg-white space-y-2">
                                  {gruppo.prodotti.map((prodotto) => (
                                    <div key={prodotto.id} className="bg-gray-50 rounded p-3">
                                      <p className="text-sm font-medium text-gray-900">
                                        {prodotto.quantita} {prodotto.tipologia_scelta ? `${prodotto.tipologia_scelta} ` : ''}{prodotto.nome}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">{prodotto.categoria}</p>
                                      {prodotto.note && (
                                        <div className="mt-2 p-2 bg-yellow-50 rounded border-l-2 border-yellow-400">
                                          <p className="text-xs text-yellow-800">
                                            <span className="font-medium">📌 Nota prodotto:</span> {prodotto.note}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  ))}

                                  {notaFornitore && (
                                    <div className="mt-3 p-3 bg-blue-50 rounded border-l-3 border-blue-400">
                                      <p className="text-xs font-medium text-blue-800 mb-1">
                                        📝 Note aggiuntive per {gruppo.fornitore.nome}:
                                      </p>
                                      <p className="text-sm text-blue-700">{notaFornitore}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 sm:px-8 sm:flex sm:flex-row-reverse flex-shrink-0 border-t border-gray-200">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="btn btn-primary"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modale aggiunta prodotti */}
      {showAddProdottiModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddProdottiModal(false)} />

            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="bg-white px-6 pt-6 pb-4 sm:p-8 sm:pb-4 flex-shrink-0 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-semibold text-gray-900">
                    Aggiungi Prodotto all'Ordine
                  </h3>
                  <button
                    onClick={closeAddProdottiModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex flex-col space-y-4 flex-1 min-h-0">
                  {/* Quick Order Input */}
                  <QuickOrderInput 
                    onProdottiParsed={handleProdottiParsed}
                    token={localStorage.getItem('token')}
                  />

                  {/* Search */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Search className="h-6 w-6 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Cerca prodotti da aggiungere..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoFocus
                      style={{ fontSize: '18px' }}
                    />
                  </div>

                  {/* Search Results */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 sticky top-0">
                        <p className="text-base font-semibold text-gray-700">Risultati ricerca ({searchResults.length}):</p>
                      </div>
                      {searchResults.map((prodotto) => (
                        <button
                          key={prodotto.id}
                          onClick={() => addProdottoToOrdine(prodotto)}
                          className="w-full px-6 py-4 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 touch-manipulation active:bg-blue-100"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-base font-semibold text-gray-900 truncate">{prodotto.nome}</p>
                              <p className="text-sm text-gray-500 truncate mt-1">
                                {prodotto.tipologia} • {prodotto.categoria}
                              </p>
                            </div>
                            <Plus className="h-6 w-6 text-blue-600 flex-shrink-0 ml-3" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Prodotti già nell'ordine - sempre visibile se ci sono prodotti */}
                  {prodottiOrdineCorrente.length > 0 && (
                    <div className={`bg-gray-50 border-2 border-gray-300 rounded-lg p-4 flex flex-col min-h-0 ${!showSearchResults ? 'flex-1' : ''}`}>
                      <h4 className="text-xl font-semibold text-gray-900 mb-4 flex-shrink-0">
                        Prodotti nell'ordine ({prodottiOrdineCorrente.length})
                      </h4>
                      <div className={`space-y-3 overflow-y-auto min-h-0 pr-2 ${!showSearchResults ? 'flex-1' : ''}`} style={{ maxHeight: showSearchResults ? 'calc(95vh - 500px)' : 'calc(95vh - 350px)', paddingBottom: '2rem', paddingTop: '0.5rem' }}>
                        {/* Ultimo prodotto inserito - sempre in alto */}
                        {getUltimoProdottoInserito() && (() => {
                          const ultimoProdotto = getUltimoProdottoInserito();
                          
                          return (
                            <div key={ultimoProdotto.id} className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 mb-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-2 py-1 rounded">
                                  ✨ Ultimo inserito
                                </span>
                              </div>
                              {renderProdottoCard(ultimoProdotto)}
                            </div>
                          );
                        })()}

                        {/* Prodotti raggruppati per fornitore */}
                        {Object.entries(groupByFornitore()).map(([fornitoreId, gruppo]) => {
                          if (!gruppo.fornitore && fornitoreId !== 'senza_fornitore') return null;
                          
                          return (
                            <div key={fornitoreId} className="mb-4">
                              {/* Header Fornitore */}
                              <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 border-b-2 border-blue-200 rounded-t-lg">
                                <div className="flex items-center">
                                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                                  <h5 className="text-sm font-semibold text-blue-900">
                                    {gruppo.fornitore ? gruppo.fornitore.nome : 'Senza fornitore'}
                                  </h5>
                                  <span className="ml-auto text-xs text-blue-600 font-medium">
                                    {gruppo.prodotti.length} prodott{gruppo.prodotti.length === 1 ? 'o' : 'i'}
                                  </span>
                                </div>
                              </div>

                              {/* Lista Prodotti per Fornitore */}
                              <div className="space-y-3 bg-white border-x-2 border-b-2 border-blue-200 rounded-b-lg p-3">
                                {gruppo.prodotti.map((prodotto) => (
                                  <div key={prodotto.id}>
                                    {renderProdottoCard(prodotto)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 sm:px-8 flex justify-between items-center flex-shrink-0 border-t border-gray-200">
                <button
                  onClick={closeAddProdottiModal}
                  className="btn btn-outline"
                >
                  Chiudi
                </button>
                <button
                  onClick={salvaModificheOrdine}
                  disabled={!prodottiModificati}
                  className={`btn ${prodottiModificati ? 'btn-primary' : 'btn-outline opacity-50 cursor-not-allowed'}`}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Salva Modifiche
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale selezione tipologia */}
      {showTipologiaModal && prodottoPendingTipologia && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-6 pt-6 pb-6 sm:p-8 sm:pb-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Seleziona Tipologia
                  </h3>
                  <button
                    onClick={() => {
                      setShowTipologiaModal(false);
                      setProdottoPendingTipologia(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-700">
                    <strong>{prodottoPendingTipologia.nome}</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    Seleziona la tipologia desiderata:
                  </p>
                </div>

                <div className="space-y-2">
                  {prodottoPendingTipologia.tipologie_disponibili?.map((tipologia) => (
                    <button
                      key={tipologia.id}
                      onClick={() => handleSelezioneTipologia(tipologia.nome)}
                      className="w-full text-left px-6 py-4 text-lg border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation"
                    >
                      <span className="font-medium">{tipologia.nome}</span>
                      {tipologia.note && (
                        <span className="text-sm text-gray-500 ml-2">• {tipologia.note}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale di conferma eliminazione prodotto */}
      <Modal
        isOpen={showDeleteProdottoModal}
        onClose={() => {
          setShowDeleteProdottoModal(false);
          setProdottoToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler rimuovere "${prodottoToDelete?.nome}" dall'ordine?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Rimuovi"
        cancelText="Annulla"
        onConfirm={executeRemoveProdotto}
      />

    </div>
  );
}

export default Monitor;
